import React, { useState, useEffect } from "react";
import { API } from "@/App";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import ImageUpload from "@/components/ui/image-upload";
import IntakeFormBuilder from "./IntakeFormBuilder";
import {
  Save,
  Eye,
  RefreshCw,
  FileText,
  Users,
  CheckCircle2,
  MessageSquare,
  ListChecks,
  Settings,
  Layout,
  Edit2,
  Copy,
  ChevronRight,
  ExternalLink,
  Sparkles,
  LayoutDashboard,
  Plus,
  Trash2,
  Link2,
  GripVertical,
  Image,
  Palette,
  Calendar,
  MapPin,
  AlignLeft,
  Mail,
  UserPlus
} from "lucide-react";

/**
 * TemplateContentEditor - Admin Template Configuration
 * 
 * This component mirrors the Host Dashboard structure exactly:
 * - Welcome (Branding, Background Theme, Display Controls)
 * - Pre-Invite (Pre-invite list settings)
 * - Intake (Intake form settings)
 * - Confirmation (Confirmation page settings)
 * - Host Dashboard (Dashboard-specific settings)
 * 
 * IMPORTANT: All image fields use ImageUpload component (NO URL inputs!)
 * See /app/memory/PLATFORM_RULES.md
 */
export default function TemplateContentEditor({ dashboardType = "instructor_pilot", onSave }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [content, setContent] = useState({});
  const [activeTab, setActiveTab] = useState("welcome");
  const [hasChanges, setHasChanges] = useState(false);

  // Fetch existing template content
  useEffect(() => {
    const fetchContent = async () => {
      try {
        const token = localStorage.getItem("session_token");
        const res = await fetch(`${API}/admin/dashboard-templates/${dashboardType}/content`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setContent(data.content || getDefaultContent());
        } else {
          setContent(getDefaultContent());
        }
      } catch (err) {
        console.error("Error fetching template content:", err);
        setContent(getDefaultContent());
      } finally {
        setLoading(false);
      }
    };
    fetchContent();
  }, [dashboardType]);

  // Default content structure matching Host Dashboard
  const getDefaultContent = () => ({
    // Welcome tab settings
    welcome: {
      envelope_animation: true,
      background_image: null,
      logo_image: null,
      show_hosted_by: false,
      show_partners: false,
      show_learn_more: false,
      learn_more_url: "",
      show_date_time: true,
      show_location: true,
      show_description: true,
      show_ways_to_customize: true,
      show_confirmation_buttons: true
    },
    // Branding settings
    branding: {
      sticker_initials: "",
      share_title: "",
      share_text: "",
      playlist_title: "",
      playlist_subtitle: "",
      accent_color: ""
    },
    // Pre-invite settings
    pre_invite: {
      list_name: "Inner Circle",
      mode_enabled: false,
      fields: {
        company: { enabled: true, required: false },
        title: { enabled: true, required: false },
        interests: { enabled: true, required: false }
      }
    },
    // Intake settings - now with form builder support
    intake: {
      title: "Complete Your Registration",
      subtitle: "Help us personalize your experience",
      // Standard field settings (show/required)
      standard_fields: {
        phone: { enabled: true, required: false },
        company: { enabled: true, required: false },
        title: { enabled: true, required: false },
        city: { enabled: true, required: false },
        interests: { enabled: true, required: false },
        dietary: { enabled: false, required: false },
        accessibility: { enabled: true, required: false }
      },
      // Custom questions added by admin
      custom_fields: [],
      // Question bank for instructors
      question_bank_enabled: false,
      question_bank_label: "Questions for the instructor?"
    },
    // Confirmation settings
    confirmation: {
      title: "You're confirmed",
      subtitle: "See you soon",
      show_edit_preferences: true,
      show_share_link: true,
      show_add_to_calendar: true,
      show_cant_make_it: true,
      instructor_section: {
        title: "Instructor Dashboard",
        subtitle: "Your session information and intake"
      },
      resources_section: {
        title: "Resources",
        subtitle: "Materials and links for this session"
      }
    },
    // Custom links
    custom_links: {
      confirmation: [],
      intake: []
    }
  });

  // Update content helper
  const updateContent = (path, value) => {
    setContent(prev => {
      const newContent = { ...prev };
      const keys = path.split(".");
      let current = newContent;
      
      for (let i = 0; i < keys.length - 1; i++) {
        if (!current[keys[i]]) current[keys[i]] = {};
        current = current[keys[i]];
      }
      
      current[keys[keys.length - 1]] = value;
      return newContent;
    });
    setHasChanges(true);
  };

  // Save content
  const saveContent = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/admin/dashboard-templates/${dashboardType}/content`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ content })
      });
      
      if (res.ok) {
        toast.success("Template saved successfully!");
        setHasChanges(false);
        if (onSave) onSave(content);
      } else {
        throw new Error("Save failed");
      }
    } catch (err) {
      console.error("Save error:", err);
      toast.error("Failed to save template");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold">Template Content Editor</h2>
          <p className="text-sm text-gray-500">
            Configure default settings for {dashboardType === "instructor_pilot" ? "Instructor Pilot" : "Event Pilot"} events
          </p>
        </div>
        <div className="flex items-center gap-2">
          {hasChanges && (
            <Badge variant="outline" className="text-orange-600 border-orange-300">
              Unsaved changes
            </Badge>
          )}
          <Button onClick={saveContent} disabled={saving || !hasChanges}>
            <Save className="w-4 h-4 mr-2" />
            {saving ? "Saving..." : "Save Template"}
          </Button>
        </div>
      </div>

      {/* Tabs - Matching Host Dashboard Structure */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-5 w-full">
          <TabsTrigger value="welcome" className="flex items-center gap-1">
            <Mail className="w-4 h-4" />
            Welcome
          </TabsTrigger>
          <TabsTrigger value="pre_invite" className="flex items-center gap-1">
            <UserPlus className="w-4 h-4" />
            Pre-Invite
          </TabsTrigger>
          <TabsTrigger value="intake" className="flex items-center gap-1">
            <FileText className="w-4 h-4" />
            Intake
          </TabsTrigger>
          <TabsTrigger value="confirmation" className="flex items-center gap-1">
            <CheckCircle2 className="w-4 h-4" />
            Confirmation
          </TabsTrigger>
          <TabsTrigger value="host_dashboard" className="flex items-center gap-1">
            <LayoutDashboard className="w-4 h-4" />
            Host Dashboard
          </TabsTrigger>
        </TabsList>

        {/* WELCOME TAB */}
        <TabsContent value="welcome" className="mt-6 space-y-6">
          {/* Background Theme */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Image className="w-5 h-5" />
                Background Theme
              </CardTitle>
              <CardDescription>
                This background appears on all attendee preference cards visible to you and your team
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <ImageUpload
                label="Background Image"
                value={content?.welcome?.background_image}
                onChange={(url) => updateContent("welcome.background_image", url)}
                description="Upload a background image for the RSVP invite page"
                previewSize="large"
                aspectRatio="banner"
              />
              
              <Separator />
              
              <ImageUpload
                label="Event Logo"
                value={content?.welcome?.logo_image}
                onChange={(url) => updateContent("welcome.logo_image", url)}
                description="Logo shown on the envelope sticker"
                previewSize="medium"
                aspectRatio="square"
              />
            </CardContent>
          </Card>

          {/* Invitation Animation */}
          <Card>
            <CardHeader>
              <CardTitle>Invitation Settings</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between p-4 rounded-lg border">
                <div>
                  <h4 className="font-medium">Invitation Intro Animation</h4>
                  <p className="text-sm text-gray-500">Show envelope animation when guests first visit</p>
                </div>
                <Switch
                  checked={content?.welcome?.envelope_animation !== false}
                  onCheckedChange={(v) => updateContent("welcome.envelope_animation", v)}
                />
              </div>
            </CardContent>
          </Card>

          {/* Display Controls */}
          <Card>
            <CardHeader>
              <CardTitle>Page Display Controls</CardTitle>
              <CardDescription>Control which sections are visible on the event page</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {[
                { key: "show_hosted_by", label: "Show 'Hosted By' Section", icon: Users },
                { key: "show_partners", label: "Show 'Event Partners' Section", icon: Users },
                { key: "show_date_time", label: "Show Date & Time", icon: Calendar },
                { key: "show_location", label: "Show Location", icon: MapPin },
                { key: "show_description", label: "Show Description", icon: AlignLeft },
                { key: "show_ways_to_customize", label: "Show 'Ways to make your experience yours'", icon: Sparkles },
                { key: "show_confirmation_buttons", label: "Show Confirmation Page Buttons", icon: CheckCircle2 }
              ].map(({ key, label, icon: Icon }) => (
                <div key={key} className="flex items-center justify-between p-3 rounded-lg border hover:bg-gray-50">
                  <div className="flex items-center gap-3">
                    <Icon className="w-4 h-4 text-gray-400" />
                    <span className="text-sm">{label}</span>
                  </div>
                  <Switch
                    checked={content?.welcome?.[key] !== false}
                    onCheckedChange={(v) => updateContent(`welcome.${key}`, v)}
                  />
                </div>
              ))}
              
              {/* Learn More Link */}
              <div className="p-3 rounded-lg border">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-3">
                    <ExternalLink className="w-4 h-4 text-gray-400" />
                    <span className="text-sm">Show "Learn More" Link</span>
                  </div>
                  <Switch
                    checked={content?.welcome?.show_learn_more === true}
                    onCheckedChange={(v) => updateContent("welcome.show_learn_more", v)}
                  />
                </div>
                {content?.welcome?.show_learn_more && (
                  <Input
                    value={content?.welcome?.learn_more_url || ""}
                    onChange={(e) => updateContent("welcome.learn_more_url", e.target.value)}
                    placeholder="https://..."
                    className="mt-2"
                  />
                )}
              </div>
            </CardContent>
          </Card>

          {/* Branding */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Palette className="w-5 h-5" />
                Branding
              </CardTitle>
              <CardDescription>Customize text displayed throughout your event experience</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Envelope Sticker</Label>
                  <Input
                    value={content?.branding?.sticker_initials || ""}
                    onChange={(e) => updateContent("branding.sticker_initials", e.target.value)}
                    placeholder="Letter Initials (e.g., HTHF)"
                  />
                  <p className="text-xs text-gray-500">Leave blank to auto-generate from event name</p>
                </div>
                <div className="space-y-2">
                  <Label>Accent Color</Label>
                  <div className="flex gap-2">
                    <Input
                      value={content?.branding?.accent_color || ""}
                      onChange={(e) => updateContent("branding.accent_color", e.target.value)}
                      placeholder="#000000"
                      className="w-32"
                    />
                    {content?.branding?.accent_color && (
                      <div 
                        className="w-10 h-10 rounded border"
                        style={{ backgroundColor: content.branding.accent_color }}
                      />
                    )}
                  </div>
                </div>
              </div>
              
              <Separator />
              
              <h4 className="font-medium text-sm">Share Messages</h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Share Title</Label>
                  <Input
                    value={content?.branding?.share_title || ""}
                    onChange={(e) => updateContent("branding.share_title", e.target.value)}
                    placeholder="Event Name - Event Pass"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Share Text</Label>
                  <Input
                    value={content?.branding?.share_text || ""}
                    onChange={(e) => updateContent("branding.share_text", e.target.value)}
                    placeholder="My pass for [Event Name]"
                  />
                </div>
              </div>
              
              <Separator />
              
              <h4 className="font-medium text-sm">Playlist Section</h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Playlist Title</Label>
                  <Input
                    value={content?.branding?.playlist_title || ""}
                    onChange={(e) => updateContent("branding.playlist_title", e.target.value)}
                    placeholder="Event Playlist"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Playlist Subtitle</Label>
                  <Input
                    value={content?.branding?.playlist_subtitle || ""}
                    onChange={(e) => updateContent("branding.playlist_subtitle", e.target.value)}
                    placeholder="Curated by [Name]"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* PRE-INVITE TAB */}
        <TabsContent value="pre_invite" className="mt-6 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <UserPlus className="w-5 h-5" />
                Pre-Invite Settings
              </CardTitle>
              <CardDescription>
                Default settings for pre-invite list management
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Default Inner Circle List Name</Label>
                <Input
                  value={content?.pre_invite?.list_name || ""}
                  onChange={(e) => updateContent("pre_invite.list_name", e.target.value)}
                  placeholder="Inner Circle"
                />
                <p className="text-xs text-gray-500">Customize how the exclusive list appears to attendees</p>
              </div>
              
              <Separator />
              
              <h4 className="font-medium text-sm">Pre-Invite Form Fields</h4>
              <p className="text-xs text-gray-500 mb-3">Select which fields appear on the pre-invite form</p>
              
              {[
                { key: "company", label: "Company" },
                { key: "title", label: "Job Title" },
                { key: "interests", label: "Interests" }
              ].map(({ key, label }) => (
                <div key={key} className="flex items-center justify-between p-3 rounded-lg border">
                  <span className="text-sm">{label}</span>
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-500">Show</span>
                      <Switch
                        checked={content?.pre_invite?.fields?.[key]?.enabled !== false}
                        onCheckedChange={(v) => updateContent(`pre_invite.fields.${key}.enabled`, v)}
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-500">Required</span>
                      <Switch
                        checked={content?.pre_invite?.fields?.[key]?.required === true}
                        onCheckedChange={(v) => updateContent(`pre_invite.fields.${key}.required`, v)}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        </TabsContent>

        {/* INTAKE TAB */}
        <TabsContent value="intake" className="mt-6 space-y-6">
          {/* Page Title/Subtitle Settings */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Intake Page Settings
              </CardTitle>
              <CardDescription>Configure the registration/intake form title and description</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Page Title</Label>
                  <Input
                    value={content?.intake?.title || ""}
                    onChange={(e) => updateContent("intake.title", e.target.value)}
                    placeholder="Complete Your Registration"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Subtitle</Label>
                  <Input
                    value={content?.intake?.subtitle || ""}
                    onChange={(e) => updateContent("intake.subtitle", e.target.value)}
                    placeholder="Help us personalize your experience"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Intake Form Builder */}
          <IntakeFormBuilder
            intakeQuestions={content?.intake?.intake_questions || []}
            defaultsInspo={content?.intake?.defaults_inspo || {}}
            onIntakeQuestionsChange={(questions) => updateContent("intake.intake_questions", questions)}
            onDefaultsInspoChange={(defaults) => updateContent("intake.defaults_inspo", defaults)}
            pilotCategory={dashboardType}
          />
          
          {/* Question Bank for Instructors */}
          {dashboardType === "instructor_pilot" && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <MessageSquare className="w-4 h-4" />
                  Question Bank (Instructor Pilots Only)
                </CardTitle>
                <CardDescription>Allow attendees to submit questions during RSVP</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center justify-between p-3 rounded-lg border">
                  <div>
                    <span className="text-sm font-medium">Enable Question Submission</span>
                    <p className="text-xs text-gray-500">Attendees can submit questions during RSVP</p>
                  </div>
                  <Switch
                    checked={content?.intake?.question_bank_enabled === true}
                    onCheckedChange={(v) => updateContent("intake.question_bank_enabled", v)}
                  />
                </div>
                {content?.intake?.question_bank_enabled && (
                  <div className="space-y-2">
                    <Label>Question Prompt</Label>
                    <Input
                      value={content?.intake?.question_bank_label || ""}
                      onChange={(e) => updateContent("intake.question_bank_label", e.target.value)}
                      placeholder="What questions do you have for the instructor?"
                    />
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* CONFIRMATION TAB */}
        <TabsContent value="confirmation" className="mt-6 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5" />
                Confirmation Page Settings
              </CardTitle>
              <CardDescription>Configure what attendees see after RSVPing</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Main Title</Label>
                  <Input
                    value={content?.confirmation?.title || ""}
                    onChange={(e) => updateContent("confirmation.title", e.target.value)}
                    placeholder="You're confirmed"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Subtitle</Label>
                  <Input
                    value={content?.confirmation?.subtitle || ""}
                    onChange={(e) => updateContent("confirmation.subtitle", e.target.value)}
                    placeholder="See you soon"
                  />
                </div>
              </div>
              
              <Separator />
              
              <h4 className="font-medium text-sm">Action Buttons</h4>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { key: "show_edit_preferences", label: "Edit Preferences" },
                  { key: "show_share_link", label: "Share Link" },
                  { key: "show_add_to_calendar", label: "Add to Calendar" },
                  { key: "show_cant_make_it", label: "Can't Make It" }
                ].map(({ key, label }) => (
                  <div key={key} className="flex items-center justify-between p-3 rounded-lg border">
                    <span className="text-sm">{label}</span>
                    <Switch
                      checked={content?.confirmation?.[key] !== false}
                      onCheckedChange={(v) => updateContent(`confirmation.${key}`, v)}
                    />
                  </div>
                ))}
              </div>
              
              {dashboardType === "instructor_pilot" && (
                <>
                  <Separator />
                  <h4 className="font-medium text-sm">Instructor Section Labels</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Instructor Section Title</Label>
                      <Input
                        value={content?.confirmation?.instructor_section?.title || ""}
                        onChange={(e) => updateContent("confirmation.instructor_section.title", e.target.value)}
                        placeholder="Instructor Dashboard"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Instructor Section Subtitle</Label>
                      <Input
                        value={content?.confirmation?.instructor_section?.subtitle || ""}
                        onChange={(e) => updateContent("confirmation.instructor_section.subtitle", e.target.value)}
                        placeholder="Your session information and intake"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Resources Section Title</Label>
                      <Input
                        value={content?.confirmation?.resources_section?.title || ""}
                        onChange={(e) => updateContent("confirmation.resources_section.title", e.target.value)}
                        placeholder="Resources"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Resources Section Subtitle</Label>
                      <Input
                        value={content?.confirmation?.resources_section?.subtitle || ""}
                        onChange={(e) => updateContent("confirmation.resources_section.subtitle", e.target.value)}
                        placeholder="Materials and links for this session"
                      />
                    </div>
                  </div>
                </>
              )}
              
              <Separator />
              
              <CustomLinksEditor 
                page="confirmation"
                links={content?.custom_links?.confirmation || []}
                onUpdate={(links) => updateContent("custom_links.confirmation", links)}
              />
            </CardContent>
          </Card>
        </TabsContent>

        {/* HOST DASHBOARD TAB */}
        <TabsContent value="host_dashboard" className="mt-6 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <LayoutDashboard className="w-5 h-5" />
                Host Dashboard Settings
              </CardTitle>
              <CardDescription>Default settings for the host dashboard interface</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="p-8 text-center text-gray-500 border-2 border-dashed rounded-lg">
                <LayoutDashboard className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <h4 className="font-medium mb-2">Host Dashboard Configuration</h4>
                <p className="text-sm">
                  Host dashboard settings are configured through the individual tabs above.
                  The Welcome, Intake, and Confirmation settings define the defaults that hosts will see.
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

// Custom Links Editor Component
function CustomLinksEditor({ page, links = [], onUpdate }) {
  const [newLink, setNewLink] = useState({ label: "", url: "", position: "resources" });

  const addLink = () => {
    if (!newLink.label || !newLink.url) {
      toast.error("Please enter both label and URL");
      return;
    }
    const updatedLinks = [...links, { ...newLink, id: `link_${Date.now()}` }];
    onUpdate(updatedLinks);
    setNewLink({ label: "", url: "", position: "resources" });
    toast.success("Link added");
  };

  const removeLink = (id) => {
    const updatedLinks = links.filter(l => l.id !== id);
    onUpdate(updatedLinks);
  };

  return (
    <div className="space-y-4">
      <h4 className="font-medium text-sm flex items-center gap-2">
        <Link2 className="w-4 h-4" />
        Custom Links
      </h4>
      <p className="text-xs text-gray-500">
        Add custom links that will appear on the {page} page
      </p>
      
      {links.length > 0 && (
        <div className="space-y-2">
          {links.map((link) => (
            <div key={link.id} className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
              <span className="text-sm flex-1 truncate">{link.label}</span>
              <Badge variant="outline" className="text-xs">{link.position}</Badge>
              <Button variant="ghost" size="sm" onClick={() => removeLink(link.id)}>
                <Trash2 className="w-4 h-4 text-red-500" />
              </Button>
            </div>
          ))}
        </div>
      )}
      
      <div className="flex gap-2">
        <Input
          value={newLink.label}
          onChange={(e) => setNewLink(p => ({ ...p, label: e.target.value }))}
          placeholder="Link label"
          className="flex-1"
        />
        <Input
          value={newLink.url}
          onChange={(e) => setNewLink(p => ({ ...p, url: e.target.value }))}
          placeholder="https://..."
          className="flex-1"
        />
        <Button size="sm" onClick={addLink}>
          <Plus className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}
